import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Calendar, Wallet, Sparkles, ArrowRight, Download,
  MapPin, Clock, CheckCircle2,
} from "lucide-react";

const brl = (v) => "R$ " + Number(v || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export default function AgencyPortal() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [billing, setBilling] = useState([]);
  const [agency, setAgency] = useState(null);

  useEffect(() => {
    api.get("/requests").then((r) => setRequests(r.data)).catch((e) => toast.error(extractError(e)));
    api.get("/billing/periods").then((r) => setBilling(r.data)).catch(() => {});
    if (user?.agency_id) {
      api.get("/agencies").then((r) => setAgency(r.data.find((a) => a.id === user.agency_id))).catch(() => {});
    }
  }, [user]);

  const today = todayIso();
  const mstart = monthStart();

  const upcoming = useMemo(() =>
    requests
      .filter((r) => ["agendado", "em_execucao"].includes(r.status) && (r.scheduled_date || "") >= today)
      .sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""))
      .slice(0, 5),
  [requests, today]);

  const completedThisMonth = useMemo(() =>
    requests.filter((r) => r.status === "concluido" && (r.scheduled_date || "") >= mstart).length,
  [requests, mstart]);

  const recentCompleted = useMemo(() =>
    requests
      .filter((r) => r.status === "concluido")
      .sort((a, b) => (b.scheduled_date || "").localeCompare(a.scheduled_date || ""))
      .slice(0, 3),
  [requests]);

  const lastOpenInvoice = useMemo(() =>
    billing
      .filter((b) => ["open", "closed"].includes(b.status))
      .sort((a, b) => (b.year * 100 + b.month) - (a.year * 100 + a.month))[0],
  [billing]);

  const totalOpen = useMemo(() =>
    billing.filter((b) => b.status === "closed").reduce((s, b) => s + (b.total || 0), 0),
  [billing]);

  const totalPaid = useMemo(() =>
    billing.filter((b) => b.status === "paid").reduce((s, b) => s + (b.total || 0), 0),
  [billing]);

  return (
    <div className="space-y-6" data-testid="agency-portal">
      {/* Hero */}
      <div className="rounded-lg bg-[#0B1F3A] text-white p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 -mt-32 -mr-32 bg-[#FFD400] opacity-10 rounded-full" />
        <div className="relative z-10">
          <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Portal · {agency?.trade_name || "sua imobiliária"}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-3">
            Olá, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-white/70 mt-2 max-w-xl">Gerencie suas vistorias e faturas em um só lugar. Precisa de uma nova vistoria?</p>
          <Link to="/solicitacoes?new=1" data-testid="portal-cta-new-request" className="inline-flex items-center gap-2 mt-5 bg-[#FFD400] text-[#0B1F3A] font-bold px-5 py-3 rounded hover:bg-yellow-300 transition-colors">
            <Sparkles size={16} /> Solicitar vistoria <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-[#E5E7EB] bg-white rounded-md p-5" data-testid="portal-kpi-upcoming">
          <div className="flex items-start justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Próximas vistorias</div>
            <div className="w-8 h-8 rounded bg-[#0B1F3A] flex items-center justify-center"><Calendar size={16} className="text-[#FFD400]" /></div>
          </div>
          <div className="mt-3 font-mono-data text-3xl font-semibold tracking-tighter text-[#0B1F3A]">{upcoming.length}</div>
          <div className="text-xs text-[#6B7280] mt-1">agendadas ou em execução</div>
        </div>
        <div className="border border-[#E5E7EB] bg-white rounded-md p-5" data-testid="portal-kpi-completed">
          <div className="flex items-start justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Concluídas no mês</div>
            <div className="w-8 h-8 rounded bg-[#FFD400] flex items-center justify-center"><CheckCircle2 size={16} className="text-[#0B1F3A]" /></div>
          </div>
          <div className="mt-3 font-mono-data text-3xl font-semibold tracking-tighter text-[#0B1F3A]">{completedThisMonth}</div>
          <div className="text-xs text-[#6B7280] mt-1">em {MONTHS[new Date().getMonth()]}</div>
        </div>
        <div className="border border-[#E5E7EB] bg-white rounded-md p-5" data-testid="portal-kpi-open">
          <div className="flex items-start justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Total em aberto</div>
            <div className="w-8 h-8 rounded bg-[#0B1F3A] flex items-center justify-center"><Wallet size={16} className="text-[#FFD400]" /></div>
          </div>
          <div className="mt-3 font-mono-data text-3xl font-semibold tracking-tighter text-[#0B1F3A]">{brl(totalOpen)}</div>
          <div className="text-xs text-[#6B7280] mt-1">{totalPaid > 0 ? `pagos: ${brl(totalPaid)}` : "\u00A0"}</div>
        </div>
      </div>

      {/* Two-column: upcoming + last invoice */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-[#E5E7EB] bg-white rounded-md">
          <div className="p-5 border-b border-[#E5E7EB]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Próximas vistorias</div>
            <div className="font-display text-lg font-bold mt-1 text-[#0B1F3A]">Sua agenda</div>
          </div>
          {upcoming.length === 0 ? (
            <div className="p-5 text-sm text-[#9CA3AF]" data-testid="portal-upcoming-empty">
              Nenhuma vistoria agendada. <Link to="/solicitacoes?new=1" className="text-[#0B1F3A] font-semibold hover:underline">Solicitar a primeira →</Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#F1F2F4]">
              {upcoming.map((r) => (
                <li key={r.id} className="p-4 hover:bg-[#F9FAFB] flex items-center justify-between" data-testid={`portal-upcoming-${r.id}`}>
                  <div>
                    <div className="text-sm font-semibold text-[#0A0A0A]">{r.address?.street}, {r.address?.number}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5 flex items-center gap-2 flex-wrap">
                      <MapPin size={11} /> {r.city_name} · <Clock size={11} /> {r.scheduled_date} · {(r.scheduled_slots || []).join(",")}
                    </div>
                  </div>
                  <Link to="/solicitacoes" className="text-[#0B1F3A] hover:text-[#081A32]"><ArrowRight size={16} /></Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-[#E5E7EB] bg-white rounded-md">
          <div className="p-5 border-b border-[#E5E7EB]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Fatura em aberto</div>
            <div className="font-display text-lg font-bold mt-1 text-[#0B1F3A]">Financeiro</div>
          </div>
          {lastOpenInvoice ? (
            <div className="p-5" data-testid="portal-invoice-card">
              <div className="text-xs text-[#6B7280] font-mono-data">{lastOpenInvoice.invoice_number}</div>
              <div className="text-sm text-[#0A0A0A] mt-1">{MONTHS[lastOpenInvoice.month - 1]} / {lastOpenInvoice.year}</div>
              <div className="mt-3">
                <div className="font-mono-data text-2xl font-bold text-[#0B1F3A] bg-[#FFD400] inline-block px-3 py-1 rounded">{brl(lastOpenInvoice.total)}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mt-2">Vencimento {lastOpenInvoice.due_date}</div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/billing/periods/${lastOpenInvoice.id}/invoice.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 px-3 py-2 bg-white border border-[#E5E7EB] rounded text-sm hover:border-[#0B1F3A]" data-testid="portal-invoice-pdf">
                <Download size={14} /> Baixar PDF
              </a>
            </div>
          ) : (
            <div className="p-5 text-sm text-[#9CA3AF]" data-testid="portal-invoice-empty">Nenhuma fatura em aberto. <Link to="/faturamento" className="text-[#0B1F3A] font-semibold hover:underline">Ver histórico →</Link></div>
          )}
        </div>
      </div>

      {/* Recent completed */}
      {recentCompleted.length > 0 && (
        <div className="border border-[#E5E7EB] bg-white rounded-md">
          <div className="p-5 border-b border-[#E5E7EB]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Laudos recentes</div>
            <div className="font-display text-lg font-bold mt-1 text-[#0B1F3A]">Últimas concluídas</div>
          </div>
          <ul className="divide-y divide-[#F1F2F4]">
            {recentCompleted.map((r) => (
              <li key={r.id} className="p-4 hover:bg-[#F9FAFB] flex items-center justify-between" data-testid={`portal-recent-${r.id}`}>
                <div>
                  <div className="text-sm font-semibold text-[#0A0A0A]">{r.address?.street}, {r.address?.number}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{r.city_name} · {r.scheduled_date} · {(r.attachments || []).length} anexo(s)</div>
                </div>
                <Link to="/solicitacoes" className="text-[#0B1F3A] hover:text-[#081A32]"><ArrowRight size={16} /></Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
